import React from 'react';

const RESULT_STYLES = {
  success: {
    background: 'rgba(52,211,153,0.1)',
    color: 'var(--success, #34d399)',
    border: '1px solid rgba(52,211,153,0.3)',
  },
  error: {
    background: 'rgba(248,113,113,0.1)',
    color: 'var(--danger, #f87171)',
    border: '1px solid rgba(248,113,113,0.3)',
  },
};

// The model sometimes echoes the prompt's example verbatim instead of naming a
// real target ("<server_hostname>"). Executing that can only fail server-side,
// so such a chip is shown as a warning instead of a button.
const PLACEHOLDER_RE = /^\s*[<{(]?\s*(server[_\s-]?(hostname|host|name|id)|hostname|host|name|id|uuid|ip|ip_address|incident_id|service_name)\s*[>})]?\s*$/i;
const BLANK_RE = /^\s*[.…*_]+x?\s*$/i;

const PARAM_LABELS = {
  server_id: 'server',
  incident_id: 'incident',
  service_name: 'service',
};

function unfilledParams(action) {
  const params = action.params || {};
  return Object.entries(params)
    .filter(([, value]) => (
      typeof value !== 'string' || !value.trim() || PLACEHOLDER_RE.test(value) || BLANK_RE.test(value)
    ))
    .map(([key]) => PARAM_LABELS[key] || key);
}

export default function ActionButtons({ content, msgIdx, onExecute, executing, results }) {
  const regex = /```brain-action\s*\n([\s\S]*?)\n```/g;
  const actions = [];
  let match;
  while ((match = regex.exec(content)) !== null) {
    try {
      const parsed = JSON.parse(match[1].trim());
      if (parsed.action) actions.push(parsed);
    } catch {}
  }
  if (actions.length === 0) return null;

  return (
    <div className="flex" style={{ gap: 8, marginTop: 8, marginLeft: 8, flexWrap: 'wrap' }}>
      {actions.map((action, i) => {
        const key = `${msgIdx}-${action.action}-${i}`;
        const isRunning = executing[key];
        const result = results[key];
        const missing = unfilledParams(action);
        const badgeStyle = {
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '6px 12px',
          borderRadius: 8,
          fontSize: 12,
          fontWeight: 500,
          border: '1px solid var(--glass-border)',
        };
        return (
          <div key={key}>
            {missing.length > 0 ? (
              <span
                style={{ ...badgeStyle, ...RESULT_STYLES.error, cursor: 'help' }}
                title={`This suggestion does not name a ${missing.join(' / ')} — ask which one to target.`}
              >
                ⚠ {action.label || action.action}
              </span>
            ) : result ? (
              <span style={{ ...badgeStyle, ...RESULT_STYLES[result.success ? 'success' : 'error'] }}>
                {result.success ? '✓' : '✕'} {action.label || action.action}
              </span>
            ) : (
              <button
                onClick={() => onExecute(msgIdx, action, i)}
                disabled={isRunning}
                style={{
                  ...badgeStyle,
                  background: 'var(--glass-strong)',
                  color: 'var(--accent-1)',
                  border: '1px solid var(--glass-border)',
                  cursor: isRunning ? 'not-allowed' : 'pointer',
                  opacity: isRunning ? 0.5 : 1,
                  transition: 'var(--transition)',
                }}
              >
                {isRunning ? (
                  <>
                    <span
                      style={{
                        width: 12,
                        height: 12,
                        border: '2px solid var(--accent-1)',
                        borderTopColor: 'transparent',
                        borderRadius: '50%',
                        animation: 'spin 0.8s linear infinite',
                      }}
                    />
                    Running...
                  </>
                ) : (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" /></svg>
                    {action.label || action.action}
                  </>
                )}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
